/**
 * A workspace's targets (2026-10-26, Claude Design « Proposition 26 octobre -
 * Profils d'environnement »): the places a session's agent checks what it
 * found — Preprod EU, Prod — besides Local, which is the projects' own
 * variables and is not stored here.
 *
 * A target gives some projects other values (`env`, by project id, only the
 * keys it changes: the rest is inherited from Local), the hosts its sessions
 * may reach on top of Network, and the secrets swapped into the requests sent
 * to those hosts. A secret is named here and sealed in each computer's
 * keychain: no value is ever in the configuration, nor in the machine — the
 * agent reads `airlock-sealed:<name>` and the host writes the value into the
 * headers on the way out, toward the secret's hosts only.
 *
 * `production` is a badge and nothing else: how the agent reaches a target is
 * the user's to choose, production included (owner, 2026-10-26).
 */
import { blockedBy, EGRESS_DOMAIN, MAX_EGRESS_DOMAINS } from './resolve';
import { domains } from './primitives';

/** A workspace keeps at most this many targets besides Local. */
export const MAX_TARGETS = 16;
export const MAX_TARGET_NAME_CHARS = 60;
/** Per project and target — the same ceiling a project's own variables have. */
export const MAX_TARGET_VARS = 200;
export const MAX_TARGET_SECRETS = 32;
export const MAX_TARGET_VALUE_CHARS = 32_000;

/**
 * How the agent reaches a target. `direct`: with its own tools, from the
 * micro-VM, its secrets substituted on the way out. `host`: never — it asks
 * for a read, run by this computer (not in this version: such a target gives
 * its sessions Local's values).
 */
export type TargetMode = 'direct' | 'host';
export const TARGET_MODES: readonly TargetMode[] = ['direct', 'host'];

/** One variable a target gives a project instead of Local's: a value, or one of its secrets. */
export type TargetVar = { key: string; value: string } | { key: string; secret: string };

export interface TargetSecret {
  /** The keychain entry's name, and the placeholder the agent reads (`airlock-sealed:<name>`). */
  name: string;
  /** Where the value is swapped in — each one of the target's `hosts`. */
  hosts: string[];
}

export interface WorkspaceTarget {
  id: string;
  name: string;
  production: boolean;
  mode: TargetMode;
  /** By project id: the variables this target gives that project instead of Local's. */
  env: Record<string, TargetVar[]>;
  /** A host by name, or `*.` and a domain for the family under it (`targetHostOk`). */
  hosts: string[];
  /**
   * What the machine may reach those hosts on besides 80 and 443 — a database's
   * own port, such as 27017 (kept absent when none). A secret is never swapped
   * into such a connection: the runtime rewrites HTTP headers only.
   */
  ports?: number[];
  secrets: TargetSecret[];
  /** Through the host: what the agent may ask to read (`env_logs`, `env_sql`, `env_http`). */
  sources: TargetSource[];
  /** Through the host: a person reads each result, already anonymized, and sends it — or not. */
  reviewResults: boolean;
  /**
   * Through the host, whether a read is asked of a person on a card (`ask`, the
   * default, kept absent) or run as soon as the agent asks for it (`auto`) —
   * owner, 2026-10-31: « choix de valider ou non par target/type opération ».
   */
  reads?: TargetReads;
  /**
   * Whether the agent may write through a source — a statement that changes a
   * database, a POST, PUT, PATCH or DELETE to an API: `off` (the default, kept
   * absent), asked on a card (`ask`), or run without asking (`auto`), never on a
   * target marked Production, which asks.
   */
  writes?: TargetWrites;
  /**
   * The organization that shares this target, by name (`org/shared-targets.ts`)
   * — set only on a target the resolver adds from an organization's settings,
   * never stored in a workspace (`normalizeTargets` does not keep it): locked
   * on the page, its secrets' values each member's own.
   */
  sharedBy?: string;
}

export type TargetReads = 'ask' | 'auto';
export type TargetWrites = 'off' | 'ask' | 'auto';

/**
 * Where a target's secret is sealed on this computer. A shared target has its
 * own place: names are the organization's to choose, and one naming a secret
 * this workspace already holds would have the member's value sent to the shared
 * target's hosts (SEC-37). `SECRET_NAME` holds no slash, so the two never meet.
 */
export function targetSecretKey(target: Pick<WorkspaceTarget, 'id' | 'sharedBy'>, name: string): string {
  return target.sharedBy ? `${target.id}/${name}` : name;
}

/**
 * Whether a target's results wait on a person whatever its own box says: on
 * Production, and where reads run without asking. The mask reads values as they
 * are written, and an agent that writes the query can re-encode them (SEC-13),
 * so there a person reading the result is the control, and the mask its aid.
 */
export function reviewedAnyway(target: Pick<WorkspaceTarget, 'production' | 'reads'>): boolean {
  return target.production || target.reads === 'auto';
}

/** How a target's reads and writes are approved, with their defaults filled in. */
export function targetApproval(target: Pick<WorkspaceTarget, 'reads' | 'writes' | 'production'>): {
  reads: TargetReads;
  writes: TargetWrites;
} {
  const writes = target.writes ?? 'off';
  return { reads: target.reads ?? 'ask', writes: target.production && writes === 'auto' ? 'ask' : writes };
}

export type TargetSourceKind = 'logs' | 'database' | 'api';
/**
 * How the host reads a source: Datadog's log search, a command of this
 * computer that prints logs (`kubectl logs`, `aws logs tail`…), Postgres
 * through `psql`, or an HTTP API.
 */
export type TargetSourceProvider = 'datadog' | 'command' | 'postgres' | 'http';
export const SOURCE_PROVIDERS: Record<TargetSourceKind, readonly TargetSourceProvider[]> = {
  logs: ['datadog', 'command'],
  database: ['postgres'],
  api: ['http']
};

export interface TargetSource {
  id: string;
  kind: TargetSourceKind;
  provider: TargetSourceProvider;
  /** What the agent names it by — `prod-logs`, `orders-db`. */
  name: string;
  /**
   * Where it is: Datadog's site (`api.datadoghq.eu`) and an optional base
   * query (`service:catalog-api`); the command (`$AIRLOCK_QUERY` holds the
   * agent's search); Postgres' host, port and database (`db.example.com:5432/orders`);
   * the API's base URL.
   */
  where: string;
  /**
   * The secret of this target that holds its credential, by name: Datadog's
   * API key, Postgres' `user:password`, the API's bearer token. None for a
   * command, which runs with this computer's own access.
   */
  secret?: string;
  /** Datadog only: the secret that holds its application key. */
  appSecret?: string;
  /** Declared, never proved: the identity can only read. Shown on every request. */
  readOnly: boolean;
}

/** A target keeps at most this many sources. */
export const MAX_TARGET_SOURCES = 8;
export const MAX_SOURCE_WHERE_CHARS = 2_000;

/** A target keeps at most this many ports besides 80 and 443. */
export const MAX_TARGET_PORTS = 8;

/**
 * Whether an entry can be one of a target's hosts: a host, or a family — a
 * replica set's names come from a DNS record nobody types, so they cannot be
 * listed. Two labels at least under the `*.`, so an entry never opens a whole
 * top-level domain.
 */
export function targetHostOk(entry: string): boolean {
  return EGRESS_DOMAIN.test(entry.startsWith('*.') ? entry.slice(2) : entry);
}

/** The hosts of a stored target: well-formed, once each, within the cap. */
function targetHostList(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  const names = value.filter((one): one is string => typeof one === 'string').map((one) => one.trim().toLowerCase());
  return [...new Set(names.filter(targetHostOk))].slice(0, MAX_EGRESS_DOMAINS);
}

/** A target's ports: whole numbers of a real port, 80 and 443 left out since every host has them. */
export function targetPortList(value: unknown): number[] {
  if (!Array.isArray(value)) return [];
  const ports = value.filter(
    (one): one is number => Number.isInteger(one) && one >= 1 && one <= 65_535 && one !== 80 && one !== 443
  );
  return [...new Set(ports)].slice(0, MAX_TARGET_PORTS);
}

/**
 * Whether an organization's blocklist takes a target's host away: the host is
 * blocked, or it is a family with a blocked name under it — the runtime's suffix
 * rule would open that name, and it cannot carve one out.
 */
export function targetHostBlocked(blocked: readonly string[], entry: string): boolean {
  if (!entry.startsWith('*.')) return blockedBy(blocked, entry);
  const family = entry.slice(2);
  return blockedBy(blocked, family) || blocked.some((one) => one.endsWith(`.${family}`));
}

/** The placeholder a session's machine holds instead of a target's secret. */
export function sealedPlaceholder(name: string): string {
  return `airlock-sealed:${name}`;
}

const TARGET_ID = /^[A-Za-z0-9_-]{1,40}$/;
const VAR_KEY = /^[^\s/=]{1,200}$/;
/** A keychain name: what a person types, kept to what reads well in a placeholder. */
export const SECRET_NAME = /^[A-Za-z0-9][A-Za-z0-9._-]{0,62}$/;

/** Keeps what is well formed and drops the rest, as every settings validator does. */
export function normalizeTargets(value: unknown): WorkspaceTarget[] {
  if (!Array.isArray(value)) return [];
  const out: WorkspaceTarget[] = [];
  for (const raw of value) {
    const one = normalizeTarget(raw);
    if (!one || out.some((t) => t.id === one.id)) continue;
    out.push(one);
    if (out.length >= MAX_TARGETS) break;
  }
  return out;
}

function normalizeTarget(raw: unknown): WorkspaceTarget | null {
  if (!raw || typeof raw !== 'object') return null;
  const t = raw as Partial<Record<keyof WorkspaceTarget, unknown>>;
  if (typeof t.id !== 'string' || !TARGET_ID.test(t.id)) return null;
  const name = typeof t.name === 'string' ? t.name.trim().replace(/\s+/g, ' ').slice(0, MAX_TARGET_NAME_CHARS) : '';
  if (!name || name.toLowerCase() === 'local') return null;
  const hosts = targetHostList(t.hosts);
  const ports = targetPortList(t.ports);
  return {
    id: t.id,
    name,
    production: t.production === true,
    mode: TARGET_MODES.includes(t.mode as TargetMode) ? (t.mode as TargetMode) : 'direct',
    env: normalizeEnv(t.env),
    hosts,
    ...(ports.length ? { ports } : {}),
    secrets: normalizeSecrets(t.secrets),
    sources: normalizeSources(t.sources),
    reviewResults: t.reviewResults === true,
    // Written only when not the default, so a document that never set them reads alike in an older version.
    ...(t.reads === 'auto' ? { reads: 'auto' as const } : {}),
    ...(t.writes === 'ask' || (t.writes === 'auto' && t.production === true)
      ? { writes: 'ask' as const }
      : t.writes === 'auto'
        ? { writes: 'auto' as const }
        : {})
  };
}

const SOURCE_NAME = /^[A-Za-z0-9][A-Za-z0-9._-]{0,62}$/;

function normalizeSources(value: unknown): TargetSource[] {
  if (!Array.isArray(value)) return [];
  const out: TargetSource[] = [];
  for (const entry of value) {
    if (!entry || typeof entry !== 'object') continue;
    const e = entry as Partial<Record<keyof TargetSource, unknown>>;
    const kind = e.kind === 'logs' || e.kind === 'database' || e.kind === 'api' ? e.kind : null;
    if (!kind || typeof e.id !== 'string' || !TARGET_ID.test(e.id) || out.some((one) => one.id === e.id)) continue;
    const provider = SOURCE_PROVIDERS[kind].includes(e.provider as TargetSourceProvider)
      ? (e.provider as TargetSourceProvider)
      : SOURCE_PROVIDERS[kind][0];
    const name = typeof e.name === 'string' ? e.name.trim().slice(0, 63) : '';
    const where = typeof e.where === 'string' ? e.where.slice(0, MAX_SOURCE_WHERE_CHARS) : '';
    const secret = typeof e.secret === 'string' && SECRET_NAME.test(e.secret) ? e.secret : undefined;
    const appSecret =
      provider === 'datadog' && typeof e.appSecret === 'string' && SECRET_NAME.test(e.appSecret) ? e.appSecret : undefined;
    out.push({
      id: e.id,
      kind,
      provider,
      name,
      where,
      ...(secret && provider !== 'command' ? { secret } : {}),
      ...(appSecret ? { appSecret } : {}),
      readOnly: e.readOnly === true
    });
    if (out.length >= MAX_TARGET_SOURCES) break;
  }
  return out;
}

function normalizeEnv(value: unknown): Record<string, TargetVar[]> {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return {};
  const out: Record<string, TargetVar[]> = {};
  for (const [project, list] of Object.entries(value as Record<string, unknown>)) {
    if (!TARGET_ID.test(project) || !Array.isArray(list)) continue;
    const vars: TargetVar[] = [];
    for (const entry of list) {
      if (!entry || typeof entry !== 'object') continue;
      const e = entry as { key?: unknown; value?: unknown; secret?: unknown };
      if (typeof e.key !== 'string' || !VAR_KEY.test(e.key) || vars.some((v) => v.key === e.key)) continue;
      if (typeof e.secret === 'string' && SECRET_NAME.test(e.secret)) vars.push({ key: e.key, secret: e.secret });
      else if (typeof e.value === 'string' && e.value.length <= MAX_TARGET_VALUE_CHARS) {
        vars.push({ key: e.key, value: e.value });
      }
      if (vars.length >= MAX_TARGET_VARS) break;
    }
    if (vars.length) out[project] = vars;
  }
  return out;
}

function normalizeSecrets(value: unknown): TargetSecret[] {
  if (!Array.isArray(value)) return [];
  const out: TargetSecret[] = [];
  for (const entry of value) {
    if (!entry || typeof entry !== 'object') continue;
    const e = entry as { name?: unknown; hosts?: unknown };
    if (typeof e.name !== 'string' || !SECRET_NAME.test(e.name) || out.some((s) => s.name === e.name)) continue;
    out.push({ name: e.name, hosts: domains(e.hosts) });
    if (out.length >= MAX_TARGET_SECRETS) break;
  }
  return out;
}

/**
 * What a Save refuses about one target, field by field — the messages Claude
 * Design wrote. Empty when it can be kept.
 */
export function targetProblems(
  target: WorkspaceTarget,
  others: WorkspaceTarget[]
): { name?: string; secrets: Record<string, string>; sources: Record<string, string> } {
  const problems: { name?: string; secrets: Record<string, string>; sources: Record<string, string> } = {
    secrets: {},
    sources: {}
  };
  const name = target.name.trim();
  if (!name) problems.name = 'Name it — the Target chip lists it by that.';
  else if (name.toLowerCase() === 'local') problems.name = 'Local is the base — give this target another name.';
  else if (others.some((o) => o.id !== target.id && o.name.trim().toLowerCase() === name.toLowerCase())) {
    problems.name = `Another target is named ${name}.`;
  }
  for (const source of target.mode === 'host' ? target.sources : []) {
    const named = (n?: string): boolean => !n || target.secrets.some((one) => one.name === n);
    if (!SOURCE_NAME.test(source.name)) {
      problems.sources[source.id] = 'Name it — letters, digits, dots and dashes: the agent asks for it by that.';
    } else if (target.sources.some((one) => one.id !== source.id && one.name === source.name)) {
      problems.sources[source.id] = `Another source is named ${source.name}.`;
    } else if (!source.where.trim()) {
      problems.sources[source.id] = 'Say where it is — this computer reads it there.';
    } else if (!named(source.secret) || !named(source.appSecret)) {
      problems.sources[source.id] = 'Its credential names no secret of this target — add it under Secrets.';
    }
  }
  for (const secret of target.mode === 'direct' ? target.secrets : []) {
    const away = secret.hosts.find((host) => !target.hosts.includes(host));
    if (away) {
      problems.secrets[secret.name] = `${away} is not one of this target’s hosts — add it under Hosts, or pick one of them.`;
    } else if (!secret.hosts.length) {
      problems.secrets[secret.name] = 'Pick the hosts it is sent to — a secret is swapped in toward them only.';
    }
  }
  return problems;
}

/** The variables of one project on one target: Local's with the target's in their place. */
export function projectEnvOn(
  local: { key: string; value: string }[],
  target: WorkspaceTarget | null,
  projectId: string
): { key: string; value: string }[] {
  const over = target && target.mode === 'direct' ? (target.env[projectId] ?? []) : [];
  if (!over.length) return local;
  const byKey = new Map(over.map((v) => [v.key, 'secret' in v ? sealedPlaceholder(v.secret) : v.value]));
  const out = local.map((v) => (byKey.has(v.key) ? { ...v, value: byKey.get(v.key) as string } : v));
  for (const v of over) if (!local.some((l) => l.key === v.key)) out.push({ key: v.key, value: byKey.get(v.key) as string });
  return out;
}
